import Phaser from 'phaser';
import type { BattleOrderType, BattlefieldModifier, BattlePoint, FormationCombatProfile, FormationConfig, ProjectileType } from '../types/battle';
import { distanceSq } from '../utils/math';
import { Battlefield } from './Battlefield';

type SoldierSprite = Phaser.GameObjects.Image | Phaser.GameObjects.Sprite;

export class Formation {
  readonly id: string;
  readonly factionId: string;
  readonly unitId: string;
  readonly side: 'attacker' | 'defender';
  readonly profile: FormationCombatProfile;
  readonly startingSoldiers: number;
  x: number;
  y: number;
  facing: number;
  morale = 100;
  fatigue = 0;
  cohesion = 100;
  alive = true;
  routed = false;
  withdrawn = false;
  selected = false;
  reserve = false;
  order: BattleOrderType = 'hold';
  targetPoint?: BattlePoint;
  targetFormation?: Formation;
  soldiers: SoldierSprite[] = [];
  banner: Phaser.GameObjects.Rectangle;
  outline: Phaser.GameObjects.Rectangle;
  facingIndicator: Phaser.GameObjects.Triangle;
  ammo: number;
  reloadRemaining = 0;
  kills = 0;
  damageReceived = 0;
  lastTerrain: BattlefieldModifier;
  private combatPower: number;
  private hitFlash?: { stop: () => void };

  constructor(private readonly scene: Phaser.Scene, config: FormationConfig, private readonly textureKey: string, private readonly battlefield: Battlefield, moraleBonus = 0, combatPower = 1) {
    this.id = config.id;
    this.factionId = config.factionId;
    this.unitId = config.unitId;
    this.side = config.side;
    this.profile = config.profile;
    this.x = config.x;
    this.y = config.y;
    this.facing = config.facing ?? (config.side === 'attacker' ? 0 : Math.PI);
    this.reserve = config.reserve ?? false;
    this.morale = Phaser.Math.Clamp(this.morale + moraleBonus, 1, 140);
    this.combatPower = Math.max(0.3, combatPower);
    this.ammo = Math.max(0, config.profile.ammo);
    this.startingSoldiers = config.soldierCount;
    this.lastTerrain = battlefield.modifierAt(this.x, this.y);
    const cols = this.preferredColumns(config.soldierCount);
    const animated = scene.textures.get(textureKey).frameTotal > 1;
    for (let index = 0; index < config.soldierCount; index++) {
      const sprite = animated ? scene.add.sprite(config.x, config.y, textureKey, 0) : scene.add.image(config.x, config.y, textureKey);
      const scale = this.profile.role === 'elephant' ? 32 : this.profile.role === 'artillery' ? 25 : 18;
      sprite.setDisplaySize(scale, scale);
      this.soldiers.push(sprite);
    }
    const width = Math.max(116, cols * 15 + 20);
    this.outline = scene.add.rectangle(this.x, this.y + 34, width, 84, 0xffffff, 0).setStrokeStyle(2, 0xf6e6bd, 0);
    this.banner = scene.add.rectangle(this.x, this.y - 26, Math.max(108, width - 20), 10, config.color, 0.92).setStrokeStyle(1, 0x111111);
    this.facingIndicator = scene.add.triangle(this.x, this.y - 40, 0, 8, 18, 0, 0, -8, 0xffdd83, 0.9);
    this.layout();
    this.playFormationAnimation('idle');
  }

  setSelected(selected: boolean) {
    this.selected = selected;
    this.outline.setStrokeStyle(selected ? 3 : 2, selected ? 0xffdf70 : 0xf6e6bd, selected ? 1 : 0);
    this.facingIndicator.setAlpha(selected ? 1 : 0.55);
  }

  setOrder(order: BattleOrderType, targetPoint?: BattlePoint, targetFormation?: Formation) {
    if (!this.alive) return;
    this.order = order;
    this.targetPoint = targetPoint;
    this.targetFormation = targetFormation;
    if (order === 'reserve') {
      this.reserve = !this.reserve;
      this.order = 'hold';
    }
    if (order === 'rally') {
      this.routed = false;
      this.morale = Math.max(32, this.morale);
      this.cohesion = Math.max(45, this.cohesion);
      this.order = 'hold';
    }
    this.playFormationAnimation(order === 'move' || order === 'charge' || order === 'retreat' || order === 'withdraw' ? 'walk' : order === 'attack' || order === 'skirmish' ? 'attack' : 'idle');
  }

  setPosition(x: number, y: number) {
    const point = this.battlefield.clampToBattle({ x, y });
    this.x = point.x;
    this.y = point.y;
    this.layout();
  }

  rotate(deltaRadians: number) {
    this.facing = Phaser.Math.Angle.Wrap(this.facing + deltaRadians);
    this.layout();
  }

  facePoint(point: BattlePoint) {
    this.facing = Math.atan2(point.y - this.y, point.x - this.x);
  }

  update(delta: number, enemies: Formation[], speedMultiplier: number, commanderAura = 0) {
    if (!this.alive) return;
    const effectiveDelta = delta * speedMultiplier;
    this.reloadRemaining = Math.max(0, this.reloadRemaining - effectiveDelta);
    this.lastTerrain = this.battlefield.modifierAt(this.x, this.y);
    if (this.order === 'withdraw' || this.order === 'retreat' || this.routed) {
      const edge = this.side === 'attacker' ? this.battlefield.bounds.x - 80 : this.battlefield.bounds.right + 80;
      this.moveToward({ x: edge, y: this.y }, effectiveDelta, this.order === 'withdraw' ? 1.12 : 1.3);
      this.morale = Math.max(1, this.morale - effectiveDelta / (this.order === 'withdraw' ? 5200 : 2600));
      if (this.x < this.battlefield.bounds.x - 30 || this.x > this.battlefield.bounds.right + 30) this.markWithdrawn();
      return;
    }
    if (this.reserve && this.order === 'hold') {
      const nearest = this.nearest(enemies);
      if (!nearest || distanceSq(this.x, this.y, nearest.x, nearest.y) > 90000) {
        this.recover(effectiveDelta, commanderAura + 0.5);
        return;
      }
      this.reserve = false;
    }
    const target = this.targetFormation?.alive ? this.targetFormation : this.nearest(enemies);
    if (!target) {
      this.recover(effectiveDelta, commanderAura);
      return;
    }
    if (this.order === 'move' && this.targetPoint) {
      this.moveToward(this.targetPoint, effectiveDelta, 1);
      if (Phaser.Math.Distance.Between(this.x, this.y, this.targetPoint.x, this.targetPoint.y) < 10) this.setOrder('hold');
      return;
    }
    const range = Phaser.Math.Distance.Between(this.x, this.y, target.x, target.y);
    const rangedRole = this.profile.maxRange > this.profile.meleeRange * 1.5 && this.profile.projectile !== 'none';
    const desiredRange = this.order === 'skirmish' && rangedRole ? this.profile.maxRange * 0.72 : rangedRole ? this.profile.maxRange * 0.82 : this.profile.meleeRange;
    if (this.order === 'hold') {
      if (rangedRole && range <= this.profile.maxRange) this.rangedAttack(target, enemies, effectiveDelta, commanderAura);
      else if (range <= this.profile.meleeRange * 1.15) this.meleeAttack(target, effectiveDelta, this.profile.role === 'spear' ? 1.08 : 0.86, commanderAura);
      else this.recover(effectiveDelta, commanderAura);
      return;
    }
    if (this.order === 'skirmish' && rangedRole) {
      if (range < desiredRange * 0.56) this.moveToward(this.awayPoint(target, 120), effectiveDelta, 0.9);
      else if (range > this.profile.maxRange * 0.95) this.moveToward(target, effectiveDelta, 0.82);
      else this.rangedAttack(target, enemies, effectiveDelta, commanderAura);
      return;
    }
    if (rangedRole && this.order !== 'charge' && this.ammo > 0) {
      if (range > desiredRange) this.moveToward(target, effectiveDelta, 0.82);
      else this.rangedAttack(target, enemies, effectiveDelta, commanderAura);
      return;
    }
    const chargeMultiplier = this.order === 'charge' ? 1.48 : 1;
    if (range > this.profile.meleeRange) this.moveToward(target, effectiveDelta, chargeMultiplier);
    else this.meleeAttack(target, effectiveDelta, chargeMultiplier, commanderAura);
  }

  liveCount() { return this.soldiers.filter((soldier) => soldier.visible).length; }
  strengthRatio() { return this.liveCount() / Math.max(1, this.startingSoldiers); }
  combatEffectiveness() { return !this.alive ? 0 : this.liveCount() * (0.35 + this.morale / 150) * (0.4 + this.cohesion / 165) * (1 - this.fatigue / 190) * this.combatPower; }
  boundsContains(x: number, y: number) { return Math.abs(x - this.x) < Math.max(80, this.outline.width / 2) && Math.abs(y - (this.y + 34)) < 64; }

  casualty(amount = 1, moraleDamage = 2, source?: Formation) {
    if (!this.alive) return;
    const live = this.soldiers.filter((soldier) => soldier.visible);
    const actual = Math.min(Math.max(0, Math.round(amount)), live.length);
    for (let index = 0; index < actual; index++) live.splice(Math.floor(Math.random() * live.length), 1)[0].setVisible(false);
    this.damageReceived += actual;
    if (source) source.kills += actual;
    this.morale = Math.max(0, this.morale - moraleDamage - actual * 0.65);
    this.cohesion = Math.max(0, this.cohesion - actual * 1.3);
    this.flashHit();
    const ratio = this.strengthRatio();
    if (!this.routed && (this.morale <= 18 || ratio < 0.3 || this.cohesion <= 10)) {
      this.routed = true;
      this.order = 'retreat';
      this.banner.setFillStyle(0x7e6c5a, 0.75);
    }
    if (this.liveCount() <= 0) this.destroyFormation();
  }

  nearest(enemies: Formation[]) {
    let best: Formation | undefined;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const enemy of enemies) {
      if (!enemy.alive || enemy.withdrawn) continue;
      const distance = distanceSq(this.x, this.y, enemy.x, enemy.y);
      if (distance < bestDistance) { best = enemy; bestDistance = distance; }
    }
    return best;
  }

  statusText() {
    const ammo = this.profile.projectile === 'none' ? '' : ` ammo ${this.ammo}`;
    const state = this.withdrawn ? 'withdrawn' : this.routed ? 'routing' : this.reserve ? 'reserve' : this.order;
    return `${this.profile.role} ${state} | morale ${Math.round(this.morale)} | cohesion ${Math.round(this.cohesion)} | fatigue ${Math.round(this.fatigue)} | ${this.lastTerrain.name}${ammo}`;
  }

  private preferredColumns(count: number) {
    if (this.profile.role === 'cavalry' || this.profile.role === 'elephant') return Math.max(4, Math.ceil(Math.sqrt(count) * 1.35));
    if (this.profile.role === 'artillery') return Math.max(4, Math.ceil(Math.sqrt(count) * 1.5));
    if (this.profile.role === 'ranged' || this.profile.role === 'gunpowder') return Math.max(6, Math.ceil(Math.sqrt(count) * 1.25));
    return Math.max(5, Math.ceil(Math.sqrt(count)));
  }

  private moveToward(point: BattlePoint | Formation, delta: number, multiplier: number) {
    const clamped = this.order === 'withdraw' || this.order === 'retreat' || this.routed
      ? { x: point.x, y: Phaser.Math.Clamp(point.y, this.battlefield.bounds.y + 12, this.battlefield.bounds.bottom - 12) }
      : this.battlefield.clampToBattle(point);
    const dx = clamped.x - this.x;
    const dy = clamped.y - this.y;
    const distance = Math.hypot(dx, dy);
    if (distance < 8) { this.playFormationAnimation('idle'); return; }
    this.facePoint(clamped);
    this.playFormationAnimation('walk');
    const terrain = this.battlefield.modifierAt(this.x, this.y);
    const fatiguePenalty = Phaser.Math.Clamp(1 - this.fatigue / 155, 0.34, 1);
    const cohesionPenalty = Phaser.Math.Clamp(this.cohesion / 100, 0.4, 1.05);
    const speed = this.profile.speed * multiplier * terrain.move * fatiguePenalty * cohesionPenalty * delta / 1000;
    this.x += dx / distance * speed;
    this.y += dy / distance * speed;
    this.fatigue = Math.min(100, this.fatigue + delta / (this.order === 'charge' ? 620 : 1700) * terrain.fatigue);
    this.cohesion = Math.max(25, this.cohesion - delta / (this.order === 'charge' ? 780 : 3300) * terrain.fatigue);
    this.layout();
  }

  private meleeAttack(target: Formation, delta: number, multiplier: number, commanderAura: number) {
    this.facePoint(target);
    this.playFormationAnimation('attack');
    const ownTerrain = this.battlefield.modifierAt(this.x, this.y);
    const targetTerrain = this.battlefield.modifierAt(target.x, target.y);
    const flank = this.flankMultiplier(target);
    const role = this.roleMatchup(target);
    const fatigue = Phaser.Math.Clamp(1 - this.fatigue / 135, 0.25, 1);
    const morale = Phaser.Math.Clamp(this.morale / 100, 0.25, 1.25);
    const cohesion = Phaser.Math.Clamp(this.cohesion / 100, 0.35, 1.15);
    const defense = Math.max(0.5, target.profile.defense * targetTerrain.defense * (1 + target.profile.armor * 0.04));
    const attack = this.profile.attack * this.combatPower * multiplier * role * flank * fatigue * morale * cohesion * (1 + commanderAura * 0.025);
    const chance = delta / 1000 * 0.68 * (attack / defense) * this.strengthRatio();
    if (Math.random() < chance) {
      const chargeShock = this.order === 'charge' ? this.profile.shock * flank : 0;
      const casualties = Math.max(1, Math.round(attack / defense * 0.7 + chargeShock * 0.18));
      target.casualty(casualties, 2.2 + chargeShock * 0.7 + (flank - 1) * 6, this);
      target.cohesion = Math.max(0, target.cohesion - 2.5 - chargeShock);
      this.fatigue = Math.min(100, this.fatigue + delta / 880 * ownTerrain.fatigue);
      if (this.profile.role === 'elephant') target.morale = Math.max(0, target.morale - 5);
    }
  }

  private rangedAttack(target: Formation, enemies: Formation[], delta: number, commanderAura: number) {
    if (this.reloadRemaining > 0 || this.ammo <= 0) { this.recover(delta, commanderAura * 0.3); return; }
    const range = Phaser.Math.Distance.Between(this.x, this.y, target.x, target.y);
    if (range > this.profile.maxRange) return;
    this.facePoint(target);
    this.playFormationAnimation('attack');
    const ownTerrain = this.battlefield.modifierAt(this.x, this.y);
    const targetTerrain = this.battlefield.modifierAt(target.x, target.y);
    const heightEdge = Phaser.Math.Clamp((ownTerrain.elevation - targetTerrain.elevation) * 0.09, -0.2, 0.24);
    const rangePenalty = Phaser.Math.Clamp(1 - range / Math.max(1, this.profile.maxRange) * 0.48, 0.42, 1);
    const coverPenalty = Phaser.Math.Clamp(1 - targetTerrain.cover, 0.2, 1);
    const fatigue = Phaser.Math.Clamp(1 - this.fatigue / 150, 0.3, 1);
    const accuracy = Phaser.Math.Clamp((0.32 + this.profile.attack * 0.035 + commanderAura * 0.012 + heightEdge) * rangePenalty * coverPenalty * ownTerrain.ranged * fatigue * (0.55 + this.cohesion / 180), 0.05, 0.92);
    this.ammo = Math.max(0, this.ammo - 1);
    this.reloadRemaining = this.profile.reloadMs * (1 + this.fatigue / 180);
    this.spawnProjectile(target, this.profile.projectile);
    if (Math.random() <= accuracy) {
      const base = this.profile.projectile === 'cannon' ? 4 : this.profile.projectile === 'shot' ? 2 : 1;
      target.casualty(Math.max(1, Math.round(base * this.combatPower * ownTerrain.ranged)), this.profile.projectile === 'cannon' ? 8 : this.profile.projectile === 'shot' ? 4.5 : 2.8, this);
      if (this.profile.projectile === 'cannon') for (const enemy of enemies.filter((enemy) => enemy !== target && enemy.alive && Phaser.Math.Distance.Between(target.x, target.y, enemy.x, enemy.y) < 125).slice(0, 2)) enemy.casualty(1, 3, this);
    }
  }

  private recover(delta: number, commanderAura: number) {
    this.playFormationAnimation('idle');
    const terrain = this.battlefield.modifierAt(this.x, this.y);
    this.fatigue = Math.max(0, this.fatigue - delta / (1700 * Math.max(0.7, terrain.fatigue)) * (1 + commanderAura * 0.04));
    this.cohesion = Math.min(100, this.cohesion + delta / 2300 * (1 + commanderAura * 0.03));
    if (!this.routed) this.morale = Math.min(120, this.morale + delta / 9000 * (1 + commanderAura * 0.05));
  }

  private roleMatchup(target: Formation) {
    if (this.profile.role === 'spear' && (target.profile.role === 'cavalry' || target.profile.role === 'elephant')) return 1.45;
    if (this.profile.role === 'cavalry' && ['ranged', 'gunpowder', 'artillery'].includes(target.profile.role)) return 1.38;
    if (this.profile.role === 'cavalry' && target.profile.role === 'spear') return 0.62;
    if (this.profile.role === 'elephant' && target.profile.role === 'spear') return 0.78;
    if (this.profile.role === 'elephant' && target.profile.role === 'melee') return 1.25;
    if (this.profile.role === 'artillery' && target.profile.role === 'cavalry') return 0.78;
    return 1;
  }

  private flankMultiplier(target: Formation) {
    const incoming = Math.atan2(this.y - target.y, this.x - target.x);
    const relative = Math.abs(Phaser.Math.Angle.Wrap(incoming - target.facing));
    if (relative > Math.PI * 0.72) return 1.55;
    if (relative > Math.PI * 0.42) return 1.28;
    return 1;
  }

  private awayPoint(target: Formation, distance: number): BattlePoint {
    const dx = this.x - target.x;
    const dy = this.y - target.y;
    const length = Math.max(1, Math.hypot(dx, dy));
    return this.battlefield.clampToBattle({ x: this.x + dx / length * distance, y: this.y + dy / length * distance });
  }

  private spawnProjectile(target: Formation, projectile: ProjectileType) {
    if (projectile === 'none') return;
    const startX = this.x + Math.cos(this.facing) * 22;
    const startY = this.y + Math.sin(this.facing) * 22;
    const color = projectile === 'arrow' ? 0xe2c077 : projectile === 'shot' ? 0xffd36f : 0xe8d4a0;
    const radius = projectile === 'cannon' ? 5 : projectile === 'shot' ? 3 : 2;
    const object = this.scene.add.circle(startX, startY, radius, color, 1).setDepth(900);
    if (projectile === 'arrow') object.setStrokeStyle(1, 0x5b3b1d, 1);
    if (projectile === 'shot' || projectile === 'cannon') {
      if (!this.playTacticalVfx('muzzle_flash', startX, startY, projectile === 'cannon' ? 1.35 : 0.85)) {
        const smoke = this.scene.add.circle(startX, startY, projectile === 'cannon' ? 18 : 10, 0xb8b8ad, 0.34).setDepth(899);
        this.scene.tweens.add({ targets: smoke, alpha: 0, scale: 1.8, duration: 500, onComplete: () => smoke.destroy() });
      }
      this.playTacticalVfx('smoke', startX, startY, projectile === 'cannon' ? 1.4 : 0.9);
    }
    const duration = projectile === 'cannon' ? 420 : projectile === 'shot' ? 220 : 520;
    this.scene.tweens.add({ targets: object, x: target.x, y: target.y, duration, ease: 'Sine.In', onComplete: () => {
      if (projectile === 'cannon' && !this.playTacticalVfx('impact', target.x, target.y, 1.4)) {
        const blast = this.scene.add.circle(target.x, target.y, 26, 0xe5b45c, 0.35).setDepth(905);
        this.scene.tweens.add({ targets: blast, alpha: 0, scale: 1.7, duration: 360, onComplete: () => blast.destroy() });
      }
      object.destroy();
    } });
  }

  private playTacticalVfx(name: 'muzzle_flash' | 'smoke' | 'dust' | 'impact', x: number, y: number, scale = 1) {
    const textureKey = 'vfx_tactical_combat';
    const animationKey = `${textureKey}:${name}`;
    if (!this.scene.textures.exists(textureKey) || !this.scene.anims.exists(animationKey)) return false;
    const sprite = this.scene.add.sprite(x, y, textureKey, 0).setDepth(910).setScale(scale);
    sprite.play(animationKey);
    sprite.once('animationcomplete', () => sprite.destroy());
    this.scene.time.delayedCall(1200, () => { if (sprite.active) sprite.destroy(); });
    return true;
  }

  private flashHit() {
    this.hitFlash?.stop();
    for (const soldier of this.soldiers) if (soldier.visible) soldier.setTint(0xffd0b0);
    this.hitFlash = this.scene.tweens.add({ targets: this.soldiers.filter((soldier) => soldier.visible), alpha: 0.72, yoyo: true, duration: 80, onComplete: () => {
      for (const soldier of this.soldiers) if (soldier.visible) soldier.clearTint().setAlpha(1);
    } });
  }

  private markWithdrawn() {
    this.withdrawn = true;
    this.alive = false;
    for (const soldier of this.soldiers) soldier.setVisible(false);
    this.banner.setVisible(false);
    this.outline.setVisible(false);
    this.facingIndicator.setVisible(false);
  }

  private destroyFormation() {
    this.alive = false;
    this.routed = true;
    for (const soldier of this.soldiers) soldier.setTint(0x666666).setAlpha(0.35);
    this.banner.setAlpha(0.25);
    this.outline.setStrokeStyle(1, 0x666666, 0.6);
    this.facingIndicator.setAlpha(0.2);
  }

  private playFormationAnimation(name: 'idle' | 'walk' | 'attack') {
    const animationKey = `${this.textureKey}:${name}`;
    if (!this.scene.anims.exists(animationKey)) return;
    for (const soldier of this.soldiers) {
      if (!(soldier instanceof Phaser.GameObjects.Sprite) || !soldier.visible) continue;
      if (soldier.anims.currentAnim?.key === animationKey && soldier.anims.isPlaying) continue;
      soldier.play(animationKey);
      if (name === 'attack') soldier.once('animationcomplete', () => { if (soldier.visible && this.scene.anims.exists(`${this.textureKey}:idle`)) soldier.play(`${this.textureKey}:idle`); });
    }
  }

  private layout() {
    const live = this.soldiers.filter((soldier) => soldier.visible);
    const cols = this.preferredColumns(Math.max(1, live.length));
    const spacingX = this.profile.role === 'elephant' ? 30 : this.profile.role === 'artillery' ? 26 : 15;
    const spacingY = this.profile.role === 'elephant' ? 28 : this.profile.role === 'artillery' ? 24 : 14;
    const cos = Math.cos(this.facing);
    const sin = Math.sin(this.facing);
    for (let index = 0; index < live.length; index++) {
      const col = index % cols;
      const row = Math.floor(index / cols);
      const localX = (col - (cols - 1) / 2) * spacingX;
      const localY = row * spacingY;
      const worldX = this.x + localX * -sin + localY * -cos;
      const worldY = this.y + localX * cos + localY * -sin;
      live[index].setPosition(worldX, worldY).setDepth(worldY);
      if (live[index] instanceof Phaser.GameObjects.Sprite) (live[index] as Phaser.GameObjects.Sprite).setFlipX(cos < 0);
    }
    const markerX = this.x - Math.cos(this.facing) * 26;
    const markerY = this.y - Math.sin(this.facing) * 26;
    this.banner.setPosition(markerX, markerY).setRotation(this.facing).setDepth(this.y + 200);
    this.outline.setPosition(this.x, this.y + 28).setRotation(this.facing).setDepth(this.y + 199);
    this.facingIndicator.setPosition(this.x + Math.cos(this.facing) * 54, this.y + Math.sin(this.facing) * 54).setRotation(this.facing).setDepth(this.y + 201);
  }
}
